// Phase 5d — the REPORT fan-curve canvas engines, ported verbatim from the
// two legacy report views:
//
//   1. drawFanDataCurve       — tabs/fan_data/view/fan_data.js drawCurves
//                               (:341-600) — the Fan Data tab's in-tab canvas
//                               AND the images captured for the single-AHU
//                               getCurve POST / the multi-AHU batched ZIP.
//   2. drawSubmittalFanCurve  — tabs/submittal_package/view/submittal.package.js
//                               drawCurves (:130-373) — the DETACHED canvases
//                               whose PNGs ride the submittal_package POST.
//
// These are DIFFERENT plots from the Phase 6 AHU-board selector engine
// (ahu-board/forms/selector/FanCurveCanvas.tsx, ported from fan.selector.js
// :5160-5479) and from each other — offsets, gridline anchors, in-canvas
// titles and the airflow midpoint source all differ:
//
//   variant     rect                x/y offset  airflow mid        titles
//   selector    (25,5,w-52,h-20)    +25/-15     airflowPerFan      outside spans
//   fan_data    (40,25,225,100)     +40/-25     model.airflow_per_fan  in-canvas
//   submittal   (25,5,w-52,h-20)    +25/-15     model.airflow_actual   none
//
// (…plus the selector's per-mfg ZA/Comefri branches, which neither report
// variant has.) Reusing the Phase 6 engine would therefore NOT be faithful;
// only the shared coefficients are reused. The point/line math below keeps
// the legacy quirks bit-for-bit — including the 'System' label whose
// coordinates compute to NaN (a no-op fillText) in BOTH report variants.
//
// Pure module: no imports from the report kit (generateReport.ts imports from
// here for the single-AHU fan_data leg — keeping this dependency one-way).

import {
  CANVAS_COEFFICIENT_X,
  CANVAS_COEFFICIENT_Y,
} from 'pages/ahu-board/forms/selector/fanSelectorData';

/** One AHUFanSelectionSerializer / AHUFanSerializer row — only the fields the
 *  curve engines touch are typed. */
export interface FanDataRow {
  id: number;
  /** AHUComponentFanCurvesPointsSerializer — sp/bhp/system_curve are JSON
   *  STRINGS ("[[cfm, y], …]"), null when unset; `curve` itself is null for
   *  an unrated fan. */
  curve?: {
    sp?: string | null;
    bhp?: string | null;
    system_curve?: string | null;
  } | null;
  airflow_per_fan?: number | null;
  airflow_actual?: number | null;
  get_si_params?: unknown;
  [key: string]: unknown;
}

type CurvePoint = [number, number];

/** The drawCurves per-model guard, identical in both views (fan_data.js:345,
 *  submittal.package.js:134). */
export function hasDrawableCurve(model: FanDataRow): boolean {
  return !!(model.curve && model.curve.sp && model.curve.system_curve);
}

/**
 * renderReport's get_si_params pass (fan_data.js:45-49): every truthy
 * get_si_params is JSON.parsed BEFORE the html() insert — a malformed value
 * THROWS and aborts the whole renderReport (single: getCurve never fires;
 * multi: that AHU renders nothing and contributes no images).
 */
export function prepareFanRows(rows: FanDataRow[]): void {
  rows.forEach((item) => {
    if (item.get_si_params) {
      item.get_si_params = JSON.parse(String(item.get_si_params));
    }
  });
}

export interface FanCanvasEntry {
  id: number;
  canvas: HTMLCanvasElement;
}

/**
 * The canvases renderReport's html() insert creates — one per fan, NO
 * width/height attributes (report.ahu.tmpl:13) → the 300×150 default, which
 * is also what submittal's document.createElement("CANVAS") yields (:412).
 */
export function createFanCanvases(rows: FanDataRow[]): FanCanvasEntry[] {
  return rows.map((row) => ({ id: row.id, canvas: document.createElement('canvas') }));
}

/**
 * drawCurves over a whole response (fan_data.js:341-383): models failing the
 * curve guard keep a BLANK canvas (still captured into img[] later). A draw
 * throw (bad curve JSON, a 1-point curve breaking the points[len-2] label)
 * propagates and ABORTS the remaining models, exactly like the legacy
 * unguarded `_.each` — callers reproduce the leg-specific consequences.
 */
export function drawFanDataCurves(entries: FanCanvasEntry[], rows: FanDataRow[]): void {
  const byId = new Map(entries.map((e) => [e.id, e.canvas]));
  rows.forEach((model) => {
    if (hasDrawableCurve(model)) {
      const canvas = byId.get(model.id);
      if (canvas) drawFanDataCurve(canvas, model);
    }
  });
}

/** getCurve/multi capture (fan_data.js:64-69/:198-203): EVERY canvas is
 *  rasterized, blank ones included. `tag` builds the id prefix — `${id}` for
 *  the single-AHU getCurve leg, `${ahuId}-${id}` for the multi-AHU batch. */
export function captureFanCanvases(
  entries: FanCanvasEntry[],
  tag: (id: number) => string
): string[] {
  return entries.map((e) => `${tag(e.id)}_${e.canvas.toDataURL()}`);
}

// ---------------------------------------------------------------------------
// Variant 1 — the Fan Data tab plot (fan_data.js:341-600).
// ---------------------------------------------------------------------------

/** One model's plot (the body of the drawCurves loop, fan_data.js:346-380).
 *  Assumes hasDrawableCurve(model); parse errors throw like the legacy. */
export function drawFanDataCurve(canvas: HTMLCanvasElement, model: FanDataRow): void {
  // :348-349 — hardcoded 300×150, matching the attribute-less canvas.
  const width = 300;
  const height = 150;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const curve = model.curve as { sp: string; bhp?: string | null; system_curve: string };

  // :352-354 — rect() without beginPath (first path op on a fresh canvas).
  ctx.rect(40, 25, 225, 100);
  ctx.fillStyle = '#f8f8f8';
  ctx.fill();

  // :355-361 — bhp is conditional; sp/system_curve parse unconditionally.
  const bhpCurve: CurvePoint[] | null = curve.bhp ? JSON.parse(curve.bhp) : null;
  const rpmCurve: CurvePoint[] = JSON.parse(curve.sp);
  const systemCurve: CurvePoint[] = JSON.parse(curve.system_curve);

  const airflowMax = Math.max(...systemCurve.map((e) => e[0]), ...rpmCurve.map((e) => e[0]));
  const SPMax = Math.max(...systemCurve.map((e) => e[1]), ...rpmCurve.map((e) => e[1]));
  // :370 — the legacy compares the RAW field (null → 0 via Math.max coercion).
  const airflowMid = Math.max(model.airflow_per_fan as unknown as number, airflowMax / 1.3);

  fdAxisX(ctx, height, airflowMid);
  fdAxisYLeft(ctx, width, height, SPMax, bhpCurve);
  fdPolyline(ctx, width, height, SPMax, airflowMid, rpmCurve, '#7cc460', 'SP');
  if (bhpCurve) {
    // :525-535 — the BHP polyline scales Y off its own max.
    const bhpMax = Math.max(...bhpCurve.map((e) => e[1]));
    fdPolyline(ctx, width, height, bhpMax, airflowMid, bhpCurve, '#a35e6b', 'BHP');
  }
  fdPolyline(ctx, width, height, SPMax, airflowMid, systemCurve, '#5b76b5', 'System');
}

/** drawAxisX (fan_data.js:385-419) — width is REASSIGNED to 250 (:386), so
 *  the axis scale is (250-50) while the curves use (300-50); the ticks land
 *  at point+15. The `.cfm_multiplier` html() update (:396) targets an element
 *  fan_data.tmpl never renders — no-op, not ported. */
function fdAxisX(ctx: CanvasRenderingContext2D, height: number, mid: number): void {
  const width = 250;
  const coeficentX = (width - 50) / (mid * CANVAS_COEFFICIENT_X);
  let cfm_multiplier = 100;
  let point = 25;
  if (mid > 50000) {
    cfm_multiplier = 1000;
  }
  const adding = Math.round((width - 25) / 8);
  for (let i = 0; i < 9; i++) {
    ctx.font = '8px Arial';
    ctx.fillStyle = '#333';
    ctx.textAlign = 'center';
    ctx.fillText(
      String(Math.round((point - 25) / (coeficentX * cfm_multiplier))),
      point + 15,
      height - 14
    );
    ctx.beginPath();
    ctx.strokeStyle = i == 0 || i == 8 ? '#000' : '#9b9b9b';
    ctx.moveTo(point + 15, 5);
    ctx.lineTo(point + 15, height - 15);
    ctx.lineWidth = i == 0 || i == 8 ? 0.5 : 0.3;
    ctx.stroke();
    point += adding;
  }
  // :415-418 — the in-canvas titles (textAlign is still 'center').
  ctx.fillText('Standard Plot with SC (total all fans)', 150, 10);
  ctx.fillText(`Airflow (CFM x ${cfm_multiplier})`, 155, 145);
}

/** drawAxisYLeft (fan_data.js:421-470) + its tail calls: the rotated axis
 *  titles and, when a BHP curve exists, the right axis (:472-487). */
function fdAxisYLeft(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  max: number,
  bhpCurve: CurvePoint[] | null
): void {
  const coeficentY = (height - 20) / (max * CANVAS_COEFFICIENT_Y);
  let point = 15;
  // :428 — one gridline per unit of SPMax (+1), point re-derived per pass.
  for (let i = 0; i <= max + 1; i++) {
    ctx.font = '8px Arial';
    ctx.fillStyle = '#333';
    ctx.textAlign = 'right';
    ctx.fillText(
      (((point - 15) * 100) / (coeficentY * 100)).toFixed(2),
      35,
      height - point - 10
    );
    ctx.beginPath();
    ctx.strokeStyle = i == 0 ? '#000' : '#9b9b9b';
    ctx.moveTo(40, height - point - 10);
    ctx.lineTo(width - 32, height - point - 10);
    ctx.lineWidth = i == 0 ? 0.5 : 0.3;
    ctx.stroke();
    point = (i + 1) * coeficentY + 15;
  }
  ctx.save();
  ctx.translate(10, 30);
  ctx.rotate(-0.5 * Math.PI);
  ctx.fillText('Static Pressure (in WC)', 0, 0);
  ctx.restore();
  ctx.save();
  ctx.translate(width, 30);
  ctx.rotate(-0.5 * Math.PI);
  if (bhpCurve) {
    ctx.fillText('BreakPower (hp)', 0, -2);
  }
  ctx.restore();
  if (bhpCurve) {
    fdAxisYRight(ctx, width, height, Math.max(...bhpCurve.map((e) => e[1])));
  }
}

/** drawAxisYRight (fan_data.js:472-487). */
function fdAxisYRight(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  max: number
): void {
  const coeficentY = (height - 20) / (max * CANVAS_COEFFICIENT_Y);
  let point = 15;
  const adding = Math.round(((height - 20) * 100) / 8) / 100;
  for (let i = 0; i < 9; i++) {
    ctx.font = '8px Arial';
    ctx.fillStyle = '#333';
    ctx.textAlign = 'left';
    ctx.fillText(
      (((point - 15) * 100) / (coeficentY * 100)).toFixed(2),
      width - 32,
      height - point - 10
    );
    point += adding;
  }
}

/**
 * drawSPCurve / drawBHPCurve / drawSystemCurve (fan_data.js:489-600) — one
 * polyline body (the three are copies differing only in Y-max source, colour
 * and label; the legacy i/j indices always coincide). +40/-25 offsets; the
 * label anchors at points[len-2] — 'System' passes the PAIR itself (:596-597),
 * so its coordinates are NaN and the fillText is a silent no-op (preserved);
 * a 1-point curve THROWS on points[-1][0] exactly like the legacy.
 */
function fdPolyline(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  yMax: number,
  mid: number,
  points: CurvePoint[],
  color: string,
  label: 'SP' | 'BHP' | 'System'
): void {
  const coeficentX = (width - 50) / (mid * CANVAS_COEFFICIENT_X);
  const coeficentY = (height - 20) / (yMax * CANVAS_COEFFICIENT_Y);
  ctx.beginPath();
  ctx.moveTo(coeficentX * points[0][0] + 40, height - coeficentY * points[0][1] - 25);
  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(coeficentX * points[i][0] + 40, height - coeficentY * points[i][1] - 25);
  }
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.25;
  ctx.stroke();

  ctx.font = '8px Arial';
  ctx.fillStyle = '#333';
  ctx.textAlign = 'left';
  if (label === 'System') {
    const pair = points[points.length - 2] as unknown as number; // NaN math (:596-597)
    ctx.fillText('System', coeficentX * pair + 30, height - coeficentY * pair - 15);
  } else {
    ctx.fillText(
      label,
      coeficentX * points[points.length - 2][0] + 30,
      height - coeficentY * points[points.length - 2][1] - 15
    );
  }
}

// ---------------------------------------------------------------------------
// Variant 2 — the Submittal Package plot (submittal.package.js:130-373).
// ---------------------------------------------------------------------------

/**
 * One model's plot (the drawCurves loop body, submittal.package.js:134-175).
 * Differences from variant 1, all legacy: canvas-sized geometry (still
 * 300×150 — createElement default), +25/-15 offsets, airflow_actual as the
 * midpoint, NO axis titles, and bhp parses UNCONDITIONALLY (:144) — a fan
 * whose bhp is absent throws, aborting the whole submittal capture (and with
 * it the submittal POST, since getCanvasData→generateReport never runs).
 */
export function drawSubmittalFanCurve(canvas: HTMLCanvasElement, model: FanDataRow): void {
  const width = canvas.width;
  const height = canvas.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const curve = model.curve as { sp: string; bhp?: string | null; system_curve: string };

  ctx.rect(25, 5, width - 52, height - 20);
  ctx.fillStyle = '#f8f8f8';
  ctx.fill();

  // :144 — JSON.parse(model.curve.bhp) with no guard: null → 'null' → null;
  // undefined → 'undefined' → SyntaxError, like the legacy.
  const bhpCurve: CurvePoint[] | null = JSON.parse(String(curve.bhp));
  const rpmCurve: CurvePoint[] = JSON.parse(curve.sp);
  const systemCurve: CurvePoint[] = JSON.parse(curve.system_curve);

  const airflowMax = Math.max(...systemCurve.map((e) => e[0]), ...rpmCurve.map((e) => e[0]));
  const SPMax = Math.max(...systemCurve.map((e) => e[1]), ...rpmCurve.map((e) => e[1]));
  const airflowMid = Math.max(model.airflow_actual as unknown as number, airflowMax / 1.3);

  spAxisX(ctx, width, height, airflowMid);
  spAxisYLeft(ctx, width, height, SPMax, bhpCurve);
  spPolyline(ctx, width, height, SPMax, airflowMid, rpmCurve, '#7cc460', 'SP');
  if (bhpCurve) {
    const bhpMax = Math.max(...bhpCurve.map((e) => e[1]));
    spPolyline(ctx, width, height, bhpMax, airflowMid, bhpCurve, '#a35e6b', 'BHP');
  }
  spPolyline(ctx, width, height, SPMax, airflowMid, systemCurve, '#5b76b5', 'System');
}

/** drawAxisX (submittal.package.js:183-212) — no width override, no titles.
 *  The `.cfm_multiplier` html() (:192) again targets nothing — not ported. */
function spAxisX(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  mid: number
): void {
  const coeficentX = (width - 50) / (mid * CANVAS_COEFFICIENT_X);
  let cfm_multiplier = 100;
  let point = 25;
  if (mid > 50000) {
    cfm_multiplier = 1000;
  }
  const adding = Math.round((width - 50) / 8);
  for (let i = 0; i < 9; i++) {
    ctx.font = '8px Arial';
    ctx.fillStyle = '#333';
    ctx.textAlign = 'center';
    ctx.fillText(
      String(Math.round((point - 25) / (coeficentX * cfm_multiplier))),
      point,
      height - 4
    );
    ctx.beginPath();
    ctx.strokeStyle = i == 0 || i == 8 ? '#000' : '#9b9b9b';
    ctx.moveTo(point, 5);
    ctx.lineTo(point, height - 15);
    ctx.lineWidth = i == 0 || i == 8 ? 0.5 : 0.3;
    ctx.stroke();
    point += adding;
  }
}

/** drawAxisYLeft (submittal.package.js:214-246) + the bhp-gated right axis
 *  (:248-263). No rotated titles in this variant. */
function spAxisYLeft(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  max: number,
  bhpCurve: CurvePoint[] | null
): void {
  const coeficentY = (height - 20) / (max * CANVAS_COEFFICIENT_Y);
  let point = 15;
  for (let i = 0; i <= max + 1; i++) {
    ctx.font = '8px Arial';
    ctx.fillStyle = '#333';
    ctx.textAlign = 'right';
    ctx.fillText(
      (((point - 15) * 100) / (coeficentY * 100)).toFixed(2),
      20,
      height - point + 2
    );
    ctx.beginPath();
    ctx.strokeStyle = i == 0 ? '#000' : '#9b9b9b';
    ctx.moveTo(25, height - point);
    ctx.lineTo(width - 27, height - point);
    ctx.lineWidth = i == 0 ? 0.5 : 0.3;
    ctx.stroke();
    point = (i + 1) * coeficentY + 15;
  }
  if (bhpCurve) {
    spAxisYRight(ctx, width, height, Math.max(...bhpCurve.map((e) => e[1])));
  }
}

/** drawAxisYRight (submittal.package.js:248-263). */
function spAxisYRight(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  max: number
): void {
  const coeficentY = (height - 20) / (max * CANVAS_COEFFICIENT_Y);
  let point = 15;
  const adding = Math.round(((height - 20) * 100) / 8) / 100;
  for (let i = 0; i < 9; i++) {
    ctx.font = '8px Arial';
    ctx.fillStyle = '#333';
    ctx.textAlign = 'left';
    ctx.fillText(
      (((point - 15) * 100) / (coeficentY * 100)).toFixed(2),
      width - 22,
      height - point + 2
    );
    point += adding;
  }
}

/** drawSPCurve/drawBHPCurve/drawSystemCurve (submittal.package.js:265-373) —
 *  +25/-15 offsets; labels anchor at points[len-2] with the same 'System'
 *  NaN quirk (:370-371) and 1-point-curve throw as variant 1. */
function spPolyline(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  yMax: number,
  mid: number,
  points: CurvePoint[],
  color: string,
  label: 'SP' | 'BHP' | 'System'
): void {
  const coeficentX = (width - 50) / (mid * CANVAS_COEFFICIENT_X);
  const coeficentY = (height - 20) / (yMax * CANVAS_COEFFICIENT_Y);
  ctx.beginPath();
  ctx.moveTo(coeficentX * points[0][0] + 25, height - coeficentY * points[0][1] - 15);
  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(coeficentX * points[i][0] + 25, height - coeficentY * points[i][1] - 15);
  }
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.25;
  ctx.stroke();

  ctx.font = '8px Arial';
  ctx.fillStyle = '#333';
  ctx.textAlign = 'left';
  if (label === 'System') {
    const pair = points[points.length - 2] as unknown as number; // NaN math (:370-371)
    ctx.fillText('System', coeficentX * pair + 30, height - coeficentY * pair - 15);
  } else {
    ctx.fillText(
      label,
      coeficentX * points[points.length - 2][0] + 30,
      height - coeficentY * points[points.length - 2][1] - 15
    );
  }
}
